import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Upload,
  DollarSign,
  Music,
  Globe,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Trash2,
  FileSpreadsheet,
  Users,
  PlayCircle,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminRoyalties() {
  const [uploads, setUploads] = useState([]);
  const [artists, setArtists] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [artistDetails, setArtistDetails] = useState(null);
  const [expandedArtists, setExpandedArtists] = useState({});
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [periodName, setPeriodName] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('artists'); // 'artists' or 'uploads'

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const [statsRes, artistsRes, uploadsRes] = await Promise.all([
        axios.get(`${API}/api/royalties/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/royalties/artists`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/royalties/uploads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setDashboardStats(statsRes.data);
      setArtists(artistsRes.data);
      setUploads(uploadsRes.data);
    } catch (error) {
      // Silent fail - data not available yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const fileInput = e.target.querySelector('input[type="file"]');
    const file = fileInput?.files?.[0];
    
    if (!file) return;
    
    setUploading(true);
    setUploadResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/api/royalties/upload${periodName ? `?period_name=${encodeURIComponent(periodName)}` : ''}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setUploadResult(response.data);
      if (response.data.success) {
        fetchData();
        setTimeout(() => {
          setUploadDialogOpen(false);
          setUploadResult(null);
          setPeriodName('');
        }, 2000);
      }
    } catch (error) {
      setUploadResult({
        success: false,
        message: error.response?.data?.detail || 'Upload failed',
        errors: [error.message]
      });
    } finally {
      setUploading(false);
    }
  };

  const fetchArtistDetails = async (artistName) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/api/royalties/artists/${encodeURIComponent(artistName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setArtistDetails(response.data);
      setSelectedArtist(artistName);
    } catch (error) {
      // Silent fail
    }
  };

  const toggleArtistExpand = (artistName) => {
    setExpandedArtists(prev => ({
      ...prev,
      [artistName]: !prev[artistName]
    }));
    
    if (!expandedArtists[artistName]) {
      fetchArtistDetails(artistName);
    }
  };

  const deleteUpload = async (uploadId) => {
    if (!window.confirm('Are you sure you want to delete this upload? All associated royalty data will be removed.')) {
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/api/royalties/uploads/${uploadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert('Failed to delete upload');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-GB').format(num || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-royalties-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-manrope">DSR Royalties</h1>
          <p className="text-muted-foreground text-sm">Upload and manage Virgin Music Group royalty statements</p>
        </div>
        
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2" data-testid="upload-dsr-btn">
              <Upload className="w-4 h-4" />
              Upload DSR
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border/50 max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                Upload DSR Statement
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleFileUpload} className="space-y-4">
              <div className="space-y-2">
                <Label>Period Name (Optional)</Label>
                <Input
                  value={periodName}
                  onChange={(e) => setPeriodName(e.target.value)}
                  placeholder="e.g., Q1 2026, January 2026"
                  className="bg-input/50"
                />
              </div>
              
              <div className="space-y-2">
                <Label>DSR File (.xlsx)</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  required
                  className="bg-input/50"
                  data-testid="dsr-file-input"
                />
                <p className="text-xs text-muted-foreground">
                  Upload a Virgin Music Group Digital Sales Report (.xlsx)
                </p>
              </div>
              
              {uploadResult && (
                <div className={`p-3 rounded-lg ${uploadResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className="flex items-center gap-2">
                    {uploadResult.success ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className={uploadResult.success ? 'text-green-500' : 'text-red-500'}>
                      {uploadResult.message}
                    </span>
                  </div>
                  {uploadResult.success && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      <p>Records: {formatNumber(uploadResult.total_records)}</p>
                      <p>Revenue: {formatCurrency(uploadResult.total_revenue)}</p>
                      <p>Artists: {uploadResult.artists_count}</p>
                    </div>
                  )}
                  {uploadResult.errors?.length > 0 && (
                    <ul className="mt-2 text-sm text-red-400">
                      {uploadResult.errors.map((err, i) => (
                        <li key={`error-${i}-${err.substring(0, 20)}`}>{err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              
              <Button type="submit" className="w-full" disabled={uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload & Process
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-green-500/10 border-green-500/30 p-4">
            <div className="flex items-center gap-2 text-green-500 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm font-medium">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(dashboardStats.total_revenue)}</p>
          </Card>
          
          <Card className="bg-blue-500/10 border-blue-500/30 p-4">
            <div className="flex items-center gap-2 text-blue-500 mb-1">
              <PlayCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Total Streams</span>
            </div>
            <p className="text-2xl font-bold">{formatNumber(dashboardStats.total_streams)}</p>
          </Card>
          
          <Card className="bg-purple-500/10 border-purple-500/30 p-4">
            <div className="flex items-center gap-2 text-purple-500 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">Artists</span>
            </div>
            <p className="text-2xl font-bold">{dashboardStats.artists_count}</p>
          </Card>
          
          <Card className="bg-orange-500/10 border-orange-500/30 p-4">
            <div className="flex items-center gap-2 text-orange-500 mb-1">
              <Music className="w-4 h-4" />
              <span className="text-sm font-medium">Tracks</span>
            </div>
            <p className="text-2xl font-bold">{dashboardStats.tracks_count}</p>
          </Card>
          
          <Card className="bg-cyan-500/10 border-cyan-500/30 p-4">
            <div className="flex items-center gap-2 text-cyan-500 mb-1">
              <FileSpreadsheet className="w-4 h-4" />
              <span className="text-sm font-medium">Uploads</span>
            </div>
            <p className="text-2xl font-bold">{dashboardStats.uploads_count}</p>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('artists')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'artists'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          By Artist
        </button>
        <button
          onClick={() => setActiveTab('uploads')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'uploads'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Uploads
        </button>
      </div>

      {/* Artists Tab */}
      {activeTab === 'artists' && (
        <div className="space-y-2">
          {artists.length === 0 ? (
            <Card className="p-8 text-center">
              <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">No Royalty Data</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Upload a DSR statement to see artist earnings
              </p>
              <Button onClick={() => setUploadDialogOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload DSR
              </Button>
            </Card>
          ) : (
            artists.map((artist) => (
              <Card key={artist.artist_name} className="overflow-hidden">
                {/* Artist Header - Clickable */}
                <div
                  className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleArtistExpand(artist.artist_name)}
                  data-testid={`artist-row-${artist.artist_name}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {expandedArtists[artist.artist_name] ? (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                      <div>
                        <p className="font-semibold">{artist.artist_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {artist.track_count} tracks • {artist.dsp_count} platforms • {artist.territory_count} territories
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-500">{formatCurrency(artist.total_revenue)}</p>
                      <p className="text-sm text-muted-foreground">{formatNumber(artist.total_streams)} streams</p>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedArtists[artist.artist_name] && artistDetails?.artist_name === artist.artist_name && (
                  <div className="border-t border-border bg-muted/10 p-4 space-y-4">
                    {/* DSP Breakdown */}
                    <div>
                      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-500" />
                        By Platform (DSP)
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(artistDetails.by_dsp || {}).slice(0, 8).map(([dsp, data]) => (
                          <div key={dsp} className="bg-card p-2 rounded-lg border border-border/50">
                            <p className="text-xs text-muted-foreground truncate">{dsp}</p>
                            <p className="font-semibold text-sm">{formatCurrency(data.revenue)}</p>
                            <p className="text-xs text-muted-foreground">{formatNumber(data.streams)} streams</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Territory Breakdown */}
                    <div>
                      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-purple-500" />
                        By Territory
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(artistDetails.by_territory || {}).slice(0, 8).map(([territory, data]) => (
                          <div key={territory} className="bg-card p-2 rounded-lg border border-border/50">
                            <p className="text-xs text-muted-foreground truncate">{territory}</p>
                            <p className="font-semibold text-sm">{formatCurrency(data.revenue)}</p>
                            <p className="text-xs text-muted-foreground">{formatNumber(data.streams)} streams</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Top Tracks */}
                    <div>
                      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <Music className="w-4 h-4 text-orange-500" />
                        Top Tracks
                      </h4>
                      <div className="space-y-1">
                        {(artistDetails.tracks || []).slice(0, 5).map((track) => (
                          <div key={track.isrc || track.track_title} className="flex items-center justify-between p-2 bg-card rounded-lg border border-border/50">
                            <div>
                              <p className="font-medium text-sm">{track.track_title || 'Unknown Track'}</p>
                              <p className="text-xs text-muted-foreground">{track.isrc}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-sm">{formatCurrency(track.revenue)}</p>
                              <p className="text-xs text-muted-foreground">{formatNumber(track.streams)} streams</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* Uploads Tab */}
      {activeTab === 'uploads' && (
        <div className="space-y-2">
          {uploads.length === 0 ? (
            <Card className="p-8 text-center">
              <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">No Uploads Yet</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Upload your first DSR statement to get started
              </p>
              <Button onClick={() => setUploadDialogOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload DSR
              </Button>
            </Card>
          ) : (
            uploads.map((upload) => (
              <Card key={upload.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{upload.period_name}</p>
                    <p className="text-sm text-muted-foreground">{upload.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      Uploaded {new Date(upload.upload_date).toLocaleDateString()} by {upload.uploaded_by_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-green-500">{formatCurrency(upload.total_revenue)}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatNumber(upload.total_records)} records • {upload.artists_count} artists
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteUpload(upload.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
